import { motion } from "framer-motion";
import { Sparkles, Trophy } from "lucide-react";

/* ───── Animated Preview: Platformer Game ───── */
const PlatformerPreview = () => (
  <div className="relative w-full h-full overflow-hidden bg-gradient-to-b from-[#87CEEB] to-[#B8E4F0]">
    {/* Clouds drifting */}
    <motion.div animate={{ x: [0, 20, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} className="absolute top-4 left-2 w-12 h-5 bg-white rounded-full opacity-80" />
    <motion.div animate={{ x: [0, 15, 0] }} transition={{ duration: 10, repeat: Infinity, ease: "linear" }} className="absolute top-3 left-8 w-7 h-4 bg-white rounded-full opacity-80" />
    <motion.div animate={{ x: [0, -18, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "linear" }} className="absolute top-8 right-4 w-10 h-4 bg-white rounded-full opacity-60" />
    {/* Ground */}
    <div className="absolute bottom-0 left-0 right-0 h-10 bg-[#5BBD5B]" />
    <div className="absolute bottom-10 left-0 right-0 h-1 bg-[#4AAE4A]" />
    {/* Platform blocks */}
    <div className="absolute bottom-10 left-6 w-10 h-7 bg-[#C4853A] rounded-sm border border-[#A06B2E]" />
    <div className="absolute left-6 w-10 h-7 bg-[#C4853A] rounded-sm border border-[#A06B2E]" style={{ bottom: 68 }} />
    <div className="absolute bottom-10 right-8 w-14 h-7 bg-[#C4853A] rounded-sm border border-[#A06B2E]" />
    {/* Character — bouncing and moving */}
    <motion.div
      animate={{ x: [0, 40, 80, 80, 40, 0], y: [0, -18, 0, 0, -18, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      className="absolute left-7 w-7 h-9 bg-[#FF6B6B] rounded-t-full rounded-b-sm"
      style={{ bottom: 76 }}
    >
      <div className="absolute top-1.5 left-1 w-2 h-1.5 bg-white rounded-full" />
      <div className="absolute top-1.5 right-1 w-2 h-1.5 bg-white rounded-full" />
    </motion.div>
    {/* Coin — pulsing & spinning */}
    <motion.div
      animate={{ scale: [1, 1.2, 1], opacity: [1, 0.6, 1] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      className="absolute right-10 w-5 h-5 bg-[#FFD700] rounded-full border-2 border-[#FFA500] flex items-center justify-center"
      style={{ bottom: 90 }}
    >
      <span className="text-[7px] font-bold text-[#A06B2E]">$</span>
    </motion.div>
    {/* Second coin appears/disappears */}
    <motion.div
      animate={{ scale: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      className="absolute left-[55%] w-5 h-5 bg-[#FFD700] rounded-full border-2 border-[#FFA500] flex items-center justify-center"
      style={{ bottom: 110 }}
    >
      <span className="text-[7px] font-bold text-[#A06B2E]">$</span>
    </motion.div>
    {/* Score — counting up */}
    <div className="absolute top-3 right-3 bg-black/30 rounded px-2 py-1">
      <motion.span
        className="text-[8px] font-bold text-white"
        key="score"
      >
        Score: <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 2, repeat: Infinity }}>150</motion.span>
      </motion.span>
    </div>
  </div>
);

/* ───── Animated Preview: Animations & Cartoons ───── */
const AnimationPreview = () => (
  <div className="relative w-full h-full overflow-hidden bg-gradient-to-b from-[#FFECD2] to-[#FCB69F]/30">
    {/* Canvas area */}
    <div className="absolute inset-3 rounded-lg bg-white border border-gray-200 shadow-inner">
      <div className="absolute inset-1 rounded bg-gradient-to-b from-[#FFECD2] to-[#FCB69F]/30 overflow-hidden">
        {/* Stars twinkling */}
        <motion.div animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }} transition={{ duration: 2, repeat: Infinity }} className="absolute top-3 left-4 text-[10px]">&#9733;</motion.div>
        <motion.div animate={{ opacity: [1, 0.3, 1], scale: [1.2, 0.8, 1.2] }} transition={{ duration: 2.5, repeat: Infinity }} className="absolute top-5 right-6 text-[8px]">&#9733;</motion.div>
        <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.8, repeat: Infinity }} className="absolute top-8 left-[45%] text-[7px]">&#9733;</motion.div>
        {/* Cat character — bouncing and waving */}
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-10 h-10 bg-[#FF9F43] rounded-full relative">
            {/* Ears */}
            <div className="absolute -top-2.5 -left-1 w-3.5 h-3.5 bg-[#FF9F43] rotate-45 rounded-tl-full" />
            <div className="absolute -top-2.5 -right-1 w-3.5 h-3.5 bg-[#FF9F43] rotate-45 rounded-tr-full" />
            {/* Eyes — blinking */}
            <motion.div
              animate={{ scaleY: [1, 0.1, 1] }}
              transition={{ duration: 3, repeat: Infinity, times: [0, 0.05, 0.1] }}
              className="absolute top-2.5 left-2 w-2 h-2 bg-[#2C3E50] rounded-full"
            />
            <motion.div
              animate={{ scaleY: [1, 0.1, 1] }}
              transition={{ duration: 3, repeat: Infinity, times: [0, 0.05, 0.1] }}
              className="absolute top-2.5 right-2 w-2 h-2 bg-[#2C3E50] rounded-full"
            />
            <div className="absolute top-5 left-1/2 -translate-x-1/2 w-1.5 h-1 bg-[#E17055] rounded-full" />
            {/* Tail wagging */}
            <motion.div
              animate={{ rotate: [-20, 20, -20] }}
              transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -right-3 top-4 w-4 h-1.5 bg-[#FF9F43] rounded-full origin-left"
            />
          </div>
        </motion.div>
      </div>
    </div>
    {/* Timeline — playhead moving */}
    <div className="absolute bottom-3 left-4 right-4 h-5 bg-gray-100 rounded border border-gray-200 flex items-center px-1.5 gap-0.5 overflow-hidden">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className={`flex-1 h-2.5 rounded-sm ${i < 4 ? 'bg-[#F28C38]' : 'bg-gray-200'}`} />
      ))}
      <motion.div
        animate={{ left: ["12%", "85%", "12%"] }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        className="absolute top-0 bottom-0 w-0.5 bg-[#E74C3C]"
      />
    </div>
  </div>
);

/* ───── Animated Preview: Interactive Stories ───── */
const StoryPreview = () => (
  <div className="relative w-full h-full overflow-hidden bg-gradient-to-b from-[#E8DAEF] to-[#F3E8FF]">
    {/* Story text — typing effect */}
    <div className="absolute top-4 left-4 right-4 bg-white rounded-lg p-3 shadow-sm border border-purple-100">
      <motion.div animate={{ width: ["0%", "100%"] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }} className="h-1.5 bg-purple-200 rounded mb-1.5 overflow-hidden" />
      <motion.div animate={{ width: ["0%", "75%"] }} transition={{ duration: 2, delay: 0.3, repeat: Infinity, repeatDelay: 2 }} className="h-1.5 bg-purple-200 rounded mb-1.5 overflow-hidden" />
      <motion.div animate={{ width: ["0%", "85%"] }} transition={{ duration: 2, delay: 0.6, repeat: Infinity, repeatDelay: 2 }} className="h-1.5 bg-purple-200 rounded overflow-hidden" />
    </div>
    {/* Character — pulsing */}
    <motion.div
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-[38%] left-1/2 -translate-x-1/2 w-9 h-9 bg-[#9333EA] rounded-full flex items-center justify-center"
    >
      <motion.span
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="text-sm text-white font-bold"
      >?</motion.span>
    </motion.div>
    {/* Choice buttons — appearing one after another */}
    <div className="absolute bottom-4 left-4 right-4 space-y-2">
      <motion.div
        animate={{ x: [-120, 0], opacity: [0, 1] }}
        transition={{ duration: 0.5, delay: 2.5, repeat: Infinity, repeatDelay: 3.5 }}
        className="bg-[#9333EA] text-white text-[8px] font-semibold rounded-lg px-3 py-2 text-center shadow-sm"
      >
        Go to the castle
      </motion.div>
      <motion.div
        animate={{ x: [120, 0], opacity: [0, 1] }}
        transition={{ duration: 0.5, delay: 3, repeat: Infinity, repeatDelay: 3.5 }}
        className="bg-white text-[#9333EA] text-[8px] font-semibold rounded-lg px-3 py-2 text-center shadow-sm border border-[#9333EA]/30"
      >
        Explore the forest
      </motion.div>
    </div>
  </div>
);

/* ───── Animated Preview: Python Code Editor ───── */
const codeLines = [
  { num: "1", parts: [{ text: "def", cls: "text-[#CBA6F7]" }, { text: " ask_question", cls: "text-[#89B4FA]" }, { text: "(q):", cls: "text-white" }] },
  { num: "2", parts: [{ text: "  answer", cls: "text-[#A6E3A1]" }, { text: " = ", cls: "text-white" }, { text: "input", cls: "text-[#FAB387]" }, { text: "(q)", cls: "text-white" }] },
  { num: "3", parts: [{ text: "  if", cls: "text-[#CBA6F7]" }, { text: " answer == ", cls: "text-white" }, { text: '"yes"', cls: "text-[#A6E3A1]" }, { text: ":", cls: "text-white" }] },
  { num: "4", parts: [{ text: "    print", cls: "text-[#FAB387]" }, { text: '("Correct!")', cls: "text-[#A6E3A1]" }] },
];

const outputLines = [
  { text: "> What is 2+2?", cls: "text-[#A6E3A1]" },
  { text: "> 4", cls: "text-white" },
  { text: "> Correct! Score: 1", cls: "text-[#A6E3A1]" },
];

const PythonPreview = () => (
  <div className="relative w-full h-full overflow-hidden bg-[#1E1E2E] rounded-sm">
    {/* Title bar */}
    <div className="flex items-center gap-1.5 px-3 py-2 bg-[#181825]">
      <div className="w-2 h-2 rounded-full bg-[#F38BA8]" />
      <div className="w-2 h-2 rounded-full bg-[#FAB387]" />
      <div className="w-2 h-2 rounded-full bg-[#A6E3A1]" />
      <span className="text-[7px] text-gray-500 ml-1.5">quiz_game.py</span>
    </div>
    {/* Code lines — appearing one by one */}
    <div className="px-3 py-2 space-y-1 font-mono">
      {codeLines.map((line, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 1, 1] }}
          transition={{ duration: 5, repeat: Infinity, times: [0, i * 0.12, i * 0.12 + 0.08, 1] }}
          className="flex gap-1.5"
        >
          <span className="text-[7px] text-gray-600 w-2">{line.num}</span>
          {line.parts.map((p, j) => (
            <span key={j} className={`text-[7px] ${p.cls}`}>{p.text}</span>
          ))}
        </motion.div>
      ))}
      <div className="h-1" />
      {/* Blinking cursor */}
      <motion.div
        animate={{ opacity: [1, 0, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="w-1.5 h-2.5 bg-[#CDD6F4] rounded-sm"
      />
      {/* Output area — lines appear after code */}
      <motion.div
        animate={{ opacity: [0, 0, 1, 1] }}
        transition={{ duration: 5, repeat: Infinity, times: [0, 0.55, 0.6, 1] }}
        className="bg-[#11111B] rounded px-2 py-1.5 mt-1"
      >
        {outputLines.map((line, i) => (
          <motion.div
            key={i}
            animate={{ opacity: [0, 0, 1, 1] }}
            transition={{ duration: 5, repeat: Infinity, times: [0, 0.6 + i * 0.08, 0.6 + i * 0.08 + 0.05, 1] }}
            className={`text-[7px] ${line.cls}`}
          >
            {line.text}
          </motion.div>
        ))}
      </motion.div>
    </div>
  </div>
);

/* ───── Project Data ───── */
const projects = [
  {
    title: "Platformer Games",
    tag: "Scratch / Game Dev",
    desc: "Side-scrolling adventures with levels, enemies, power-ups, and scoring systems — fully playable.",
    details: ["Custom characters & sprites", "Multiple game levels", "Score tracking & sound effects"],
    color: "#1677FF",
    preview: <PlatformerPreview />,
  },
  {
    title: "Animations & Cartoons",
    tag: "Digital Creativity",
    desc: "Original frame-by-frame animated shorts with characters, scenes, dialogue, and sound design elements.",
    details: ["Original character design", "Scene transitions & timing", "Sound effects & voiceover"],
    color: "#F28C38",
    preview: <AnimationPreview />,
  },
  {
    title: "Interactive Stories",
    tag: "Scratch / Storytelling",
    desc: "Choose-your-own-adventure experiences where readers make decisions that change the story outcome.",
    details: ["Branching story paths", "Character dialogue system", "Multiple endings"],
    color: "#9333EA",
    preview: <StoryPreview />,
  },
  {
    title: "Python Apps & Tools",
    tag: "Python Programming",
    desc: "Real programs students write from scratch — quiz games, calculators, chatbots, and simple interactive mini-apps.",
    details: ["Working command-line apps", "Data handling & logic", "Problem-solving with real code"],
    color: "#00A676",
    preview: <PythonPreview />,
  },
];

/* ───── Section Component ───── */
export const StudentProjects = () => {
  return (
    <section data-testid="student-projects-section" className="pt-[35px] pb-[35px] md:py-24 bg-[#FBFBF7]">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#F3E8FF] text-[#9333EA] mb-4">
            <Trophy className="w-3 h-3" /> Real Results
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">
            Real Projects Students{" "}
            <span className="text-[#9333EA]">Build</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            Every course ends with a real project your child can proudly share with family and friends. Students learn by building games, animations, interactive stories, and real coding projects.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="group relative bg-white rounded-2xl border border-border overflow-hidden card-hover"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Left: Animated preview */}
                <div className="sm:w-[200px] h-[200px] sm:h-auto flex-shrink-0 relative overflow-hidden">
                  {project.preview}
                </div>

                {/* Right: Content */}
                <div className="flex-1 p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ backgroundColor: project.color }}
                    >
                      {project.tag}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold mb-2">{project.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 max-w-[90%] md:max-w-none">{project.desc}</p>

                  <div className="space-y-1.5">
                    {project.details.map((detail) => (
                      <div key={detail} className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 flex-shrink-0" style={{ color: project.color }} />
                        <span className="text-xs text-muted-foreground">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StudentProjects;
